import React, { useEffect, useRef, useState } from 'react';
import * as d3Selection from 'd3-selection';
import * as d3Scale from 'd3-scale';
import * as d3ScaleChromatic from 'd3-scale-chromatic';
import * as d3Force from 'd3-force';
import * as d3Drag from 'd3-drag';
import * as d3Zoom from 'd3-zoom';
import * as d3Transition from 'd3-transition';
import * as d3Brush from 'd3-brush';
import * as d3Color from 'd3-color';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  ButtonGroup,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  TableSortLabel,
  TextField,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import TableChartIcon from '@mui/icons-material/TableChart';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MapIcon from '@mui/icons-material/Map';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import NetworkGraphMap from './NetworkGraphMap';

// Combine d3 modules
const d3 = {
  ...d3Selection,
  ...d3Scale,
  ...d3ScaleChromatic,
  ...d3Force,
  ...d3Drag,
  ...d3Zoom,
  ...d3Transition,
  ...d3Brush,
  ...d3Color,
};

interface Node extends d3Force.SimulationNodeDatum {
  id: string;
  type?: string;
  group?: number;
  label?: string;
  entity_type?: string;
  place_name?: string;
  place_type?: string;
  place_address?: string;
  domain?: string;  // For website nodes
  sample_url?: string;  // For website nodes
}

interface Link extends d3Force.SimulationLinkDatum<Node> {
  source: string | Node;
  target: string | Node;
  weight?: number;
  type?: string;
  // SMS specific fields
  comms_direction?: string;
  sms_number?: string;
  sms_message?: string;
  // Call specific fields
  comms_logs_type?: string;
  call_logs_number?: string;
  call_logs_duration?: number;
  // Browsing specific fields
  url?: string;
  // Common timestamp
  timestamp?: string;
}

interface LinkAnalysisSettings {
  nodeSize?: number;
  linkDistance?: number;
  chargeStrength?: number;
  linkCurvature?: number;
  linkOpacity?: number;
}

interface ThemeColors {
  primary: string;
  secondary: string;
  accent?: string;
}

interface NetworkGraphProps {
  nodes: Node[];
  links: Link[];
  width?: number;
  height?: number;
  onNodeClick?: (node: Node) => void;
  onSelectionChange?: (selectedNodes: Node[], autoExport?: boolean) => void;
  selectionMode?: boolean;
  proximityDistance?: number;
  onProximityDistanceChange?: (value: number) => void;
  timeWindow?: number;
  onTimeWindowChange?: (value: number) => void;
  onRefreshAnalysis?: () => void;
  onExportSubgraph?: (subgraphNodes: Node[], subgraphLinks: Link[], centerNode: Node) => void;
  // User appearance settings
  appearanceSettings?: LinkAnalysisSettings;
  // Theme colors from user settings
  themeColors?: ThemeColors;
  // Context menu callbacks
  onSaveDevice?: (deviceId: string, deviceLabel: string) => void;
  onAddDeviceNote?: (deviceId: string, deviceLabel: string) => void;
  onSaveAsGeofence?: (location: { lat: number; lng: number; name: string; placeType?: string }) => void;
  onAddLocationNote?: (location: { lat: number; lng: number; name: string }) => void;
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({
  nodes,
  links,
  width = 0,
  height = 0,
  onNodeClick,
  onSelectionChange,
  selectionMode = false,
  proximityDistance,
  onProximityDistanceChange,
  timeWindow,
  onRefreshAnalysis,
  onTimeWindowChange,
  onExportSubgraph,
  appearanceSettings,
  themeColors,
  onSaveDevice,
  onAddDeviceNote,
  onSaveAsGeofence,
  onAddLocationNote,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphAreaRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<any>(null);
  const brushRef = useRef<any>(null);
  const savedTransformRef = useRef<any>(null); // Store zoom transform to preserve it across re-renders
  const [dimensions, setDimensions] = useState({ width: width || 1200, height: height || 700 });
  const [colorScheme, setColorScheme] = useState<string>('category');
  // Use appearance settings if provided, otherwise use defaults
  const [linkDistance, setLinkDistance] = useState<number>(appearanceSettings?.linkDistance ?? 100);
  const [chargeStrength, setChargeStrength] = useState<number>(appearanceSettings?.chargeStrength ?? -300);
  const [curvature, setCurvature] = useState<number>(appearanceSettings?.linkCurvature ?? 0.3);
  const [nodeSize, setNodeSize] = useState<number>(appearanceSettings?.nodeSize ?? 8);
  const [linkOpacity, setLinkOpacity] = useState<number>(appearanceSettings?.linkOpacity ?? 0.8);
  const [stats, setStats] = useState<{ nodes: number; links: number; originalNodes?: number; originalLinks?: number }>({ nodes: 0, links: 0 });
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [selectionModeActive, setSelectionModeActive] = useState(selectionMode);
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [showOnlyKnownDevices, setShowOnlyKnownDevices] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Context menu state for node right-click
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; node: Node } | null>(null);

  // Sorting state for each table
  type SortOrder = 'asc' | 'desc';
  const [nodesSort, setNodesSort] = useState<{ field: string; order: SortOrder }>({ field: 'id', order: 'asc' });

  // Sync selection mode with parent prop
  useEffect(() => {
    setSelectionModeActive(selectionMode);
  }, [selectionMode]);

  // Sync appearance settings when they change from parent
  useEffect(() => {
    if (appearanceSettings) {
      if (appearanceSettings.linkDistance !== undefined) setLinkDistance(appearanceSettings.linkDistance);
      if (appearanceSettings.chargeStrength !== undefined) setChargeStrength(appearanceSettings.chargeStrength);
      if (appearanceSettings.linkCurvature !== undefined) setCurvature(appearanceSettings.linkCurvature);
      if (appearanceSettings.nodeSize !== undefined) setNodeSize(appearanceSettings.nodeSize);
      if (appearanceSettings.linkOpacity !== undefined) setLinkOpacity(appearanceSettings.linkOpacity);
    }
  }, [appearanceSettings]);

  // Dynamically size the graph based on the visible graph area (exclude side panel)
  useEffect(() => {
    const targetEl = graphAreaRef.current || containerRef.current;
    if (!targetEl) return;
    
    const updateDimensions = () => {
      if (graphAreaRef.current || containerRef.current) {
        const rect = (graphAreaRef.current || containerRef.current)!.getBoundingClientRect();
        setDimensions({
          width: rect.width || 1200,
          height: rect.height || 700
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [/* Recalculate when side panel toggles to capture width changes */ sidePanelOpen]);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const { width: graphWidth, height: graphHeight } = dimensions;

    // Save current zoom transform before clearing (to preserve zoom position)
    if (svgRef.current) {
      const currentTransform = d3.zoomTransform(svgRef.current);
      if (currentTransform && (currentTransform.k !== 1 || currentTransform.x !== 0 || currentTransform.y !== 0)) {
        savedTransformRef.current = currentTransform;
      }
    }

    // Filter nodes and links based on toggle
    let filteredNodes = nodes;
    let filteredLinks = links;
    
    if (showOnlyKnownDevices) {
      // Filter out phone number nodes (but keep all device nodes)
      filteredNodes = nodes.filter(node => {
        const id = node.id;
        // Remove nodes that start with "phone:" (unknown phone numbers)
        return !(typeof id === 'string' && id.startsWith('phone:'));
      });
      
      // Filter out links that involve phone numbers
      // Keep only device-to-device connections (browsing, dwells)
      filteredLinks = links.filter(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        // Keep link only if BOTH source and target are NOT phone numbers
        const sourceIsPhone = typeof sourceId === 'string' && sourceId.startsWith('phone:');
        const targetIsPhone = typeof targetId === 'string' && targetId.startsWith('phone:');
        return !sourceIsPhone && !targetIsPhone;
      });
    }

    // PERFORMANCE: Limit nodes/links for large graphs to prevent browser freeze
    const MAX_NODES = 500;
    const MAX_LINKS = 2000;
    
    if (filteredNodes.length > MAX_NODES) {
      // Keep nodes with most connections (higher degree nodes are more important)
      const nodeDegreeMap = new Map<string, number>();
      filteredNodes.forEach(n => nodeDegreeMap.set(n.id, 0));
      filteredLinks.forEach((l: any) => {
        const src = typeof l.source === 'string' ? l.source : l.source?.id;
        const tgt = typeof l.target === 'string' ? l.target : l.target?.id;
        nodeDegreeMap.set(src, (nodeDegreeMap.get(src) || 0) + 1);
        nodeDegreeMap.set(tgt, (nodeDegreeMap.get(tgt) || 0) + 1);
      });
      // Sort by degree and keep top nodes
      filteredNodes = [...filteredNodes]
        .sort((a, b) => (nodeDegreeMap.get(b.id) || 0) - (nodeDegreeMap.get(a.id) || 0))
        .slice(0, MAX_NODES);
      const nodeIdSet = new Set(filteredNodes.map(n => n.id));
      // Filter links to only include kept nodes
      filteredLinks = filteredLinks.filter((l: any) => {
        const src = typeof l.source === 'string' ? l.source : l.source?.id;
        const tgt = typeof l.target === 'string' ? l.target : l.target?.id;
        return nodeIdSet.has(src) && nodeIdSet.has(tgt);
      });
    }
    
    if (filteredLinks.length > MAX_LINKS) {
      // Keep links with highest weight (stronger connections)
      filteredLinks = [...filteredLinks]
        .sort((a: any, b: any) => (b.weight || 1) - (a.weight || 1))
        .slice(0, MAX_LINKS);
    }

    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();

    // Update stats with filtered counts (show warning if limited)
    const wasLimited = nodes.length > MAX_NODES || links.length > MAX_LINKS;
    setStats({ 
      nodes: filteredNodes.length, 
      links: filteredLinks.length,
      ...(wasLimited && { originalNodes: nodes.length, originalLinks: links.length })
    });

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr('width', graphWidth)
      .attr('height', graphHeight)
      .attr('viewBox', `0 0 ${graphWidth} ${graphHeight}`);

    // Add zoom behavior
    const g = svg.append('g').attr('class', 'graph-container');
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 10])
      .on('zoom', (event: any) => {
        g.attr('transform', event.transform);
      });

    // Always enable zoom/pan
    svg.call(zoom);

    // Click on background to deselect all nodes
    svg.on('click', () => {
      if (selectedNodes.size > 0) {
        setSelectedNodes(new Set());
        if (onSelectionChange) {
          onSelectionChange([]);
        }
      }
    });

    // Store zoom for controls
    zoomRef.current = zoom;

    // Restore previous zoom transform if it exists (preserves zoom when toggling selection mode)
    // Otherwise, set initial zoom to be zoomed out and centered
    if (savedTransformRef.current) {
      svg.call(zoom.transform as any, savedTransformRef.current);
    } else {
      // Initial load: zoom out to 0.75x to see the whole graph
      const initialTransform = d3.zoomIdentity
        .translate(graphWidth / 2, graphHeight / 2)  // Center on canvas
        .scale(0.25)                                  // Zoom out to 75%
        .translate(-graphWidth / 2, -graphHeight / 2); // Translate back
      svg.call(zoom.transform as any, initialTransform);
    }

    // Add brush for selection (on top layer, after zoom)
    let brushGroup: any = null;
    if (selectionModeActive) {
      // Disable zoom during brush interaction to prevent conflicts
      const brush = d3
        .brush()
        .extent([[0, 0], [graphWidth, graphHeight]])
        .on('start', () => {
          // Disable zoom during brush
          svg.on('.zoom', null);
        })
        .on('end', (event: any) => {
          // Re-enable zoom after brush
          svg.call(zoom);
          
          if (!event.selection) return;
          
          const [[x0, y0], [x1, y1]] = event.selection;
          const selected = new Set<string>();
          
          // Get current transform to adjust coordinates
          const transform = d3.zoomTransform(svg.node() as any);
          
          // Find nodes within selection box (accounting for zoom/pan)
          nodes.forEach((node: any) => {
            const screenX = node.x * transform.k + transform.x;
            const screenY = node.y * transform.k + transform.y;
            if (screenX >= x0 && screenX <= x1 && screenY >= y0 && screenY <= y1) {
              selected.add(node.id);
            }
          });
          
          setSelectedNodes(selected);
          if (onSelectionChange) {
            // Auto-export for box selections
            onSelectionChange(nodes.filter(n => selected.has(n.id)), true);
          }
          
          // Clear brush after selection
          brushGroup.call(brush.move, null);
        });

      brushGroup = svg.append('g').attr('class', 'brush').call(brush);
      brushRef.current = brush;
    }

    // Color scales - Use theme colors if provided, otherwise fallback to defaults
    const primary = themeColors?.primary || '#2E86AB';
    const secondary = themeColors?.secondary || '#A23B72';
    const accent = themeColors?.accent || '#00f0ff';
    
    // Generate a palette from theme colors with variations
    const colorScale = d3.scaleOrdinal([
      primary,
      secondary, 
      accent,
      // Add variations by adjusting brightness
      d3.color(primary)?.brighter(0.5)?.formatHex() || '#4A9BC4',
      d3.color(secondary)?.brighter(0.5)?.formatHex() || '#C05D92',
      d3.color(primary)?.darker(0.3)?.formatHex() || '#1F5C75',
      d3.color(secondary)?.darker(0.3)?.formatHex() || '#6E284E',
      accent,
    ]);
    
    // Type color map using theme colors
    const typeColorMap: Record<string, string> = {
      call: primary,           // Primary color for calls
      sms: secondary,          // Secondary color for SMS
      browsing: accent,        // Accent color for browsing
      dwell: d3.color(secondary)?.brighter(0.3)?.formatHex() || secondary, // Lighter secondary for dwells
      device: primary,         // Primary for devices
      phone: d3.color(primary)?.brighter(0.3)?.formatHex() || primary,  // Lighter primary for phones
      location: secondary,     // Secondary for locations
      website: accent,         // Accent for websites
    };

    // Calculate node degrees (number of connections)
    const nodeDegrees = new Map<string, number>();
    filteredNodes.forEach(node => nodeDegrees.set(node.id, 0));
    filteredLinks.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      nodeDegrees.set(sourceId, (nodeDegrees.get(sourceId) || 0) + 1);
      nodeDegrees.set(targetId, (nodeDegrees.get(targetId) || 0) + 1);
    });

    // Find nodes with highest degrees
    const maxDegree = Math.max(...Array.from(nodeDegrees.values()));
    const highDegreeNodes = filteredNodes.filter(node => 
      (nodeDegrees.get(node.id) || 0) > maxDegree * 0.5
    );

    // Calculate center of mass for high-degree nodes
    let centerX = graphWidth / 2;
    let centerY = graphHeight / 2;
    if (highDegreeNodes.length > 0) {
      // Initially position high-degree nodes near center
      highDegreeNodes.forEach(node => {
        if (node.x === undefined) {
          node.x = centerX + (Math.random() - 0.5) * 50;
          node.y = centerY + (Math.random() - 0.5) * 50;
        }
      });
    }

    // Create force simulation with performance tuning based on graph size
    const isLargeGraph = filteredNodes.length > 200;
    const simulation = d3
      .forceSimulation<Node>(filteredNodes)
      .force(
        'link',
        d3
          .forceLink<Node, Link>(filteredLinks)
          .id((d: any) => d.id)
          .distance(linkDistance)
      )
      .force('charge', d3.forceManyBody().strength(chargeStrength))
      .force('center', d3.forceCenter(centerX, centerY))
      .force('collision', d3.forceCollide().radius(isLargeGraph ? 10 : 15))
      // PERFORMANCE: Faster convergence for large graphs
      .alphaDecay(isLargeGraph ? 0.05 : 0.0228)
      .velocityDecay(isLargeGraph ? 0.6 : 0.4);

    // Create arrow markers for directed edges (smaller, cleaner arrows)
    svg
      .append('defs')
      .selectAll('marker')
      .data(['call', 'sms', 'browsing', 'dwell'])
      .join('marker')
      .attr('id', (d: any) => `arrow-${d}`)
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 15)  // Adjusted to stop before node (node radius is 8)
      .attr('refY', 5)   // Centered vertically
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', 'M0,0 L0,10 L10,5 z')  // Cleaner arrow shape
      .attr('fill', (d: any) => typeColorMap[d] || '#999');

    // Add glow filter for neon effect
    const defs = svg.select('defs');
    const filter = defs.append('filter')
      .attr('id', 'neon-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '2')
      .attr('result', 'coloredBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Create links with curves
    const link = g
      .append('g')
      .selectAll('path')
      .data(filteredLinks)
      .join('path')
      .attr('fill', 'none')
      .attr('stroke', (d: any) => typeColorMap[d.type] || '#00f0ff')
      .attr('stroke-opacity', linkOpacity)
      .attr('stroke-width', (d: any) => {
        // Cap stroke width so dwell connections don't become too thick
        const weight = d.weight || 1;
        return Math.min(Math.sqrt(weight) * 0.8, 2); // Slightly thicker for neon visibility
      })
      .style('filter', 'url(#neon-glow)')
      .attr('marker-end', (d: any) => (d.type ? `url(#arrow-${d.type})` : null));

    // Create node groups
    const node = g
      .append('g')
      .selectAll('g')
      .data(filteredNodes)
      .join('g')
      .attr('class', 'node-group');
    
    // Only enable dragging when not in selection mode
    if (!selectionModeActive) {
      node.call(
        d3
          .drag<any, any>()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended)
      );
    }

    // Add circles for nodes (size increases when selected)
    node
      .append('circle')
      .attr('r', (d: any) => selectedNodes.has(d.id) ? nodeSize * 2 : nodeSize)
      .attr('fill', (d: any) => {
        if (colorScheme === 'type' && d.type) {
          return typeColorMap[d.type] || colorScale(d.group || 0);
        }
        return colorScale(d.group || 0);
      })
      .attr('stroke', (d: any) => selectedNodes.has(d.id) ? '#ff9800' : '#fff')
      .attr('stroke-width', (d: any) => selectedNodes.has(d.id) ? 4 : 1)
      .style('cursor', selectionModeActive ? 'crosshair' : 'pointer')
      .on('click', (event: any, d: any) => {
        event.stopPropagation();
        
        // Toggle node selection on click
        const newSelected = new Set(selectedNodes);
        if (newSelected.has(d.id)) {
          // Deselect if already selected
          newSelected.delete(d.id);
        } else {
          // Select the clicked node (clear others unless Ctrl is held)
          if (!event.ctrlKey && !selectionModeActive) {
            newSelected.clear();
          }
          newSelected.add(d.id);
        }
        setSelectedNodes(newSelected);
        
        // Notify parent of selection change
        if (onSelectionChange) {
          onSelectionChange(nodes.filter(n => newSelected.has(n.id)));
        }
        
        // Also trigger onNodeClick callback
        if (onNodeClick) {
          onNodeClick(d as Node);
        }
      })
      .on('contextmenu', (event: any, d: any) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
          mouseX: event.clientX,
          mouseY: event.clientY,
          node: d as Node
        });
      })
      .on('mouseover', function (this: any, _: any, d: any) {
        d3.select(this).transition().duration(200).attr('r', nodeSize * 1.4);

        // Highlight connected nodes
        const connectedNodes = new Set<string>();
        links.forEach((l: any) => {
          if (l.source.id === d.id) connectedNodes.add(l.target.id);
          if (l.target.id === d.id) connectedNodes.add(l.source.id);
        });

        node
          .select('circle')
          .transition()
          .duration(200)
          .style('opacity', (n: any) => (connectedNodes.has(n.id) || n.id === d.id ? 1 : 0.2));

        link
          .transition()
          .duration(200)
          .style('opacity', (l: any) =>
            l.source.id === d.id || l.target.id === d.id ? 1 : 0.1
          );
      })
      .on('mouseout', function (this: any, _: any, d: any) {
        // Reset size unless this node is selected
        d3.select(this).transition().duration(200).attr('r', selectedNodes.has(d.id) ? nodeSize * 2 : nodeSize);

        // If there are selected nodes, restore to selection-based highlighting
        if (selectedNodes.size > 0) {
          // Build set of all nodes connected to selected nodes
          const connectedToSelected = new Set<string>();
          selectedNodes.forEach(selectedId => {
            connectedToSelected.add(selectedId);
            links.forEach((l: any) => {
              const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
              const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
              if (sourceId === selectedId) connectedToSelected.add(targetId);
              if (targetId === selectedId) connectedToSelected.add(sourceId);
            });
          });

          node.select('circle').transition().duration(200)
            .style('opacity', (n: any) => connectedToSelected.has(n.id) ? 1 : 0.2);
          
          link.transition().duration(200)
            .style('opacity', (l: any) => {
              const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
              const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
              return selectedNodes.has(sourceId) || selectedNodes.has(targetId) ? 1 : 0.1;
            });
        } else {
          // No selection, reset everything to normal
          node.select('circle').transition().duration(200).style('opacity', 1);
          link.transition().duration(200).style('opacity', 0.8);
        }
      });

    // Add labels
    node
      .append('text')
      .text((d: any) => {
        let id = d.id;
        let label = d.label || id;
        
        // For phone nodes, ensure we show just the number without the "phone:" prefix
        if (d.type === 'phone' || (typeof id === 'string' && id.startsWith('phone:'))) {
          // Remove phone: prefix from id if label fell back to id
          if (typeof label === 'string' && label.startsWith('phone:')) {
            label = label.replace('phone:', '');
          }
          // Ensure we have a label (should already be set by backend, but just in case)
          if (!label || label === id) {
            label = typeof id === 'string' ? id.replace('phone:', '') : id;
          }
          return label;
        }
        
        // For location nodes (loc_XX.XXXX_-YY.YYYY format), use the name if available
        if (typeof label === 'string' && label.startsWith('loc_') && label.match(/loc_[-\d.]+_[-\d.]+/)) {
          // Check if node has a name property (from geocoding)
          if (d.name && d.name !== label) {
            label = d.name;
          } else if (d.place_name && d.place_name !== label) {
            label = d.place_name;
          } else {
            // Extract coordinates for display
            const coords = label.replace('loc_', '').split('_');
            label = `Location (${coords[0].slice(0, 6)}, ${coords[1].slice(0, 7)})`;
          }
        }
        
        // Smart abbreviation for long labels
        if (label.length > 20) {
          // Remove common words to shorten
          let abbreviated = label
            .replace(/\b(Street|Avenue|Road|Drive|Lane|Boulevard|Plaza|Center|Market|North|South|East|West)\b/gi, '')
            .trim();
          
          // If still too long, truncate
          if (abbreviated.length > 18) {
            abbreviated = abbreviated.substring(0, 15) + '...';
          }
          
          label = abbreviated || label.substring(0, 15) + '...';
        }
        
        return label;
      })
      .attr('x', 0)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#e6edf3')
      .style('pointer-events', 'none')
      .style('user-select', 'none');

    // Add tooltips
    node.append('title').text((d: any) => {
      let text = `ID: ${d.id}`;
      if (d.type) text += `\nType: ${d.type}`;
      if (d.group) text += `\nGroup: ${d.group}`;
      return text;
    });

    link.append('title').text((d: any) => {
      let text = `${d.source.id} Ã¢â€ ' ${d.target.id}`;
      if (d.type) text += `\nType: ${d.type}`;
      if (d.weight) text += `\nWeight: ${d.weight}`;
      return text;
    });

    // Apply persistent selection highlighting (like hover but stays)
    if (selectedNodes.size > 0) {
      // Build set of all nodes connected to selected nodes
      const connectedToSelected = new Set<string>();
      selectedNodes.forEach(selectedId => {
        connectedToSelected.add(selectedId);
        filteredLinks.forEach((l: any) => {
          const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
          const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
          if (sourceId === selectedId) connectedToSelected.add(targetId);
          if (targetId === selectedId) connectedToSelected.add(sourceId);
        });
      });

      // Dim non-connected nodes
      node
        .select('circle')
        .style('opacity', (n: any) => connectedToSelected.has(n.id) ? 1 : 0.2);
      
      // Dim node labels too
      node
        .select('text')
        .style('opacity', (n: any) => connectedToSelected.has(n.id) ? 1 : 0.2);

      // Dim non-connected links
      link.style('opacity', (l: any) => {
        const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
        const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
        return selectedNodes.has(sourceId) || selectedNodes.has(targetId) ? 1 : 0.1;
      });
    }

    // Update positions on simulation tick
    simulation.on('tick', () => {
      // Allow nodes to expand beyond bounds for more freedom
      // No constraints applied - nodes can move anywhere
      
      // Draw curved paths
      link.attr('d', (d: any) => {
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Shorten the line to stop at node edges (node radius = 8)
        const nodeRadius = 8;
        const sourceX = d.source.x + (dx / dist) * nodeRadius;
        const sourceY = d.source.y + (dy / dist) * nodeRadius;
        const targetX = d.target.x - (dx / dist) * nodeRadius;
        const targetY = d.target.y - (dy / dist) * nodeRadius;
        
        // Create a curve by offsetting the control point
        // Use a quadratic bezier curve for smooth arcs
        const offsetX = -dy * curvature; // Perpendicular offset
        const offsetY = dx * curvature;
        const midX = (sourceX + targetX) / 2 + offsetX;
        const midY = (sourceY + targetY) / 2 + offsetY;
        
        return `M${sourceX},${sourceY} Q${midX},${midY} ${targetX},${targetY}`;
      });

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // Drag functions (no bounds constraints)
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      // Allow dragging anywhere without constraints
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [nodes, links, dimensions, colorScheme, linkDistance, chargeStrength, curvature, nodeSize, linkOpacity, onNodeClick, selectedNodes, selectionModeActive, onSelectionChange, showOnlyKnownDevices, themeColors]);

  const handleZoomIn = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    zoomRef.current.scaleBy(svg.transition().duration(300), 1.3);
  };

  const handleZoomOut = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    zoomRef.current.scaleBy(svg.transition().duration(300), 1 / 1.3);
  };

  const handleZoomReset = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    zoomRef.current.transform(svg.transition().duration(750), d3.zoomIdentity);
  };

  const handleToggleSelectionMode = () => {
    setSelectionModeActive(!selectionModeActive);
    if (selectionModeActive) {
      // Clear selection when exiting selection mode
      setSelectedNodes(new Set());
      if (onSelectionChange) onSelectionChange([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedNodes(new Set());
    if (onSelectionChange) onSelectionChange([]);
  };

  const handleTableNodeClick = (nodeId: string) => {
    // Toggle node selection and highlight in graph
    const newSelected = new Set(selectedNodes);
    if (newSelected.has(nodeId)) {
      newSelected.delete(nodeId);
    } else {
      // Clear previous selections and select only this node
      newSelected.clear();
      newSelected.add(nodeId);
    }
    setSelectedNodes(newSelected);
    
    // Notify parent if callback exists
    if (onSelectionChange) {
      onSelectionChange(nodes.filter(n => newSelected.has(n.id)));
    }

    // Pan and zoom to the selected node (with delay to let graph re-render)
    setTimeout(() => {
      if (svgRef.current && zoomRef.current) {
        const svg = d3.select(svgRef.current);
        const allNodes = svg.selectAll('g.node-group');
        
        // Find the node data
        let targetNodeData: any = null;
        allNodes.each(function(this: any, d: any) {
          if (d.id === nodeId) {
            targetNodeData = d;
          }
        });
        
        if (targetNodeData && targetNodeData.x !== undefined && targetNodeData.y !== undefined) {
          // Calculate transform to center and zoom WAY in on the node
          const scale = 4; // Much higher zoom level for spread out graphs
          const x = dimensions.width / 2 - targetNodeData.x * scale;
          const y = dimensions.height / 2 - targetNodeData.y * scale;
          
          const transform = d3.zoomIdentity.translate(x, y).scale(scale);
          
          // Animate to the node
          svg.transition()
            .duration(1000)
            .call(zoomRef.current.transform, transform)
            .on('end', () => {
              // Add pulsing highlight ring AFTER zoom completes, at screen center
              svg.select('g.graph-container')
                .append('circle')
                .attr('cx', targetNodeData.x)
                .attr('cy', targetNodeData.y)
                .attr('r', 20)
                .attr('fill', 'none')
                .attr('stroke', '#ff9800')
                .attr('stroke-width', 4)
                .attr('opacity', 1)
                .transition()
                .duration(1500)
                .attr('r', 50)
                .attr('opacity', 0)
                .remove();
            });
        }
      }
    }, 100); // Small delay to ensure graph has updated
  };

  // Handle exporting a node and its connections as a subset graph
  const handleExportSubgraph = (centerNode: Node) => {
    const centerNodeId = centerNode.id;
    
    // Helper to get ID from source/target (handles both string and object formats)
    const getLinkNodeId = (nodeRef: any): string => {
      if (typeof nodeRef === 'string') return nodeRef;
      if (nodeRef && typeof nodeRef === 'object') {
        return nodeRef.id || nodeRef.index?.toString() || '';
      }
      return '';
    };
    
    // First pass: Find all directly connected node IDs
    const connectedNodeIds = new Set<string>([centerNodeId]);
    
    links.forEach((link: any) => {
      const sourceId = getLinkNodeId(link.source);
      const targetId = getLinkNodeId(link.target);
      
      if (sourceId === centerNodeId) connectedNodeIds.add(targetId);
      if (targetId === centerNodeId) connectedNodeIds.add(sourceId);
    });
    
    // Second pass: Find ALL links between the connected nodes
    const connectedLinks: Link[] = [];
    links.forEach((link: any) => {
      const sourceId = getLinkNodeId(link.source);
      const targetId = getLinkNodeId(link.target);
      
      if (connectedNodeIds.has(sourceId) && connectedNodeIds.has(targetId)) {
        connectedLinks.push({
          ...link,
          source: sourceId,
          target: targetId
        });
      }
    });
    
    // Get the node objects - create clean copies
    const connectedNodes = nodes
      .filter(n => connectedNodeIds.has(n.id))
      .map(n => ({ ...n }));
    
    // Close context menu
    setContextMenu(null);
    
    // Call the export callback if provided
    if (onExportSubgraph) {
      onExportSubgraph(connectedNodes, connectedLinks, centerNode);
    }
  };

  // Handle closing context menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // CSV export helper function
  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','), // Header row
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma or quote
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export nodes to CSV
  const exportNodes = () => {
    const data = nodes.map((node) => {
      const degree = links.filter((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        return sourceId === node.id || targetId === node.id;
      }).length;

      return {
        'Node ID': node.id,
        'Type': node.type || '-',
        'Group': node.group || '-',
        'Connections': degree
      };
    });
    exportToCSV(data, 'network-graph-nodes.csv');
  };

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'row'
      }}
    >
      {/* Main Graph Area */}
      <Box sx={{ 
        flex: 1, 
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s'
      }}>

        {/* Graph */}
        <Paper elevation={2} sx={{ 
          flex: 1,
          overflow: 'hidden', 
          bgcolor: '#0d1117',
          display: 'flex',
          width: '100%',
          height: '100%'
        }} ref={graphAreaRef}>
          {nodes.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography variant="h6" sx={{ color: '#8b949e' }}>
                No network data available. Adjust filters or date range.
              </Typography>
            </Box>
          ) : (
            <svg 
              ref={svgRef} 
              style={{ 
                width: '100%', 
                height: '100%', 
                border: selectionModeActive ? '2px solid #ff9800' : '1px solid #30363d',
                cursor: selectionModeActive ? 'crosshair' : 'grab',
              }} 
            />
          )}
        </Paper>
      </Box>

      {/* Right Side Panel */}
      <Paper
        elevation={3}
        sx={{
          width: sidePanelOpen ? 800 : 48,
          height: '100%',
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderLeft: 1,
          borderColor: 'divider'
        }}
      >
        {/* Toggle Button */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            minHeight: 56
          }}
        >
          {sidePanelOpen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
              <TableChartIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold">
                Graph Data
              </Typography>
              <Chip 
                label={stats.originalNodes ? `${stats.nodes}/${stats.originalNodes} Nodes` : `${stats.nodes} Nodes`} 
                size="small" 
                variant="outlined"
                color={stats.originalNodes ? "warning" : "primary"}
                title={stats.originalNodes ? `Showing ${stats.nodes} of ${stats.originalNodes} nodes (limited for performance)` : undefined}
              />
              <Chip 
                label={stats.originalLinks ? `${stats.links}/${stats.originalLinks} Links` : `${stats.links} Links`} 
                size="small" 
                variant="outlined"
                color={stats.originalLinks ? "warning" : "secondary"}
                title={stats.originalLinks ? `Showing ${stats.links} of ${stats.originalLinks} links (limited for performance)` : undefined}
              />
            </Box>
          )}
          <IconButton 
            size="small"
            onClick={() => setSidePanelOpen(!sidePanelOpen)}
            sx={{ ml: sidePanelOpen ? 0 : 'auto', mr: sidePanelOpen ? 0 : 'auto' }}
          >
            {sidePanelOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* Panel Content */}
        {sidePanelOpen && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header with Search and Export Button - Only show for Nodes tab */}
            {activeTab === 0 && (
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, bgcolor: 'background.default' }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ minWidth: 180, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportNodes}
                  disabled={nodes.length === 0}
                >
                  Export CSV
                </Button>
              </Box>
            )}

            <Tabs 
              value={activeTab} 
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 48 }}
            >
              <Tab label={stats.originalNodes ? `Nodes (${stats.nodes}/${stats.originalNodes})` : `Nodes (${stats.nodes})`} />
              <Tab label="Map" icon={<MapIcon />} iconPosition="start" />
              <Tab label="Controls" />
              <Tab label="Help" />
            </Tabs>

            {/* Nodes Tab */}
            {activeTab === 0 && (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={nodesSort.field === 'id'}
                            direction={nodesSort.order}
                            onClick={() => setNodesSort({ field: 'id', order: nodesSort.field === 'id' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                          >
                            <strong>Node ID</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={nodesSort.field === 'label'}
                            direction={nodesSort.order}
                            onClick={() => setNodesSort({ field: 'label', order: nodesSort.field === 'label' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                          >
                            <strong>Label</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={nodesSort.field === 'type'}
                            direction={nodesSort.order}
                            onClick={() => setNodesSort({ field: 'type', order: nodesSort.field === 'type' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                          >
                            <strong>Type</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={nodesSort.field === 'entity_type'}
                            direction={nodesSort.order}
                            onClick={() => setNodesSort({ field: 'entity_type', order: nodesSort.field === 'entity_type' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                          >
                            <strong>Entity Type</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <strong>Location/Category</strong>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={nodesSort.field === 'connections'}
                            direction={nodesSort.order}
                            onClick={() => setNodesSort({ field: 'connections', order: nodesSort.field === 'connections' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                          >
                            <strong>Connections</strong>
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...nodes]
                        .map((node) => {
                          const degree = links.filter((link: any) => {
                            const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
                            const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
                            return sourceId === node.id || targetId === node.id;
                          }).length;
                          return { ...node, connections: degree };
                        })
                        .filter((node) => {
                          if (!searchQuery) return true;
                          const query = searchQuery.toLowerCase();
                          const nodeId = node.id.startsWith && node.id.startsWith('phone:') ? 
                            node.id.replace('phone:', '') : node.id;
                          return nodeId.toLowerCase().includes(query) ||
                                 (node.type || '').toLowerCase().includes(query) ||
                                 (node.label || '').toLowerCase().includes(query) ||
                                 (node.entity_type || '').toLowerCase().includes(query) ||
                                 (node.place_name || '').toLowerCase().includes(query) ||
                                 (node.place_type || '').toLowerCase().includes(query);
                        })
                        .sort((a, b) => {
                          const field = nodesSort.field;
                          const order = nodesSort.order === 'asc' ? 1 : -1;
                          
                          if (field === 'connections') {
                            return (a.connections - b.connections) * order;
                          }
                          
                          const aVal = String(a[field as keyof typeof a] || '');
                          const bVal = String(b[field as keyof typeof b] || '');
                          return aVal.localeCompare(bVal) * order;
                        })
                        .map((node) => {
                          // Get display label
                          const displayLabel = node.label || 
                                              (node.id.startsWith && node.id.startsWith('phone:') ? node.id.replace('phone:', '') : node.id);
                          
                          return (
                          <TableRow 
                            key={node.id}
                            onClick={() => handleTableNodeClick(node.id)}
            sx={{
                              bgcolor: selectedNodes.has(node.id) ? 'action.selected' : 'inherit',
                              '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' }
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {node.id.startsWith && node.id.startsWith('phone:') ? 
                                node.id.replace('phone:', '') : node.id}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: node.label ? 'bold' : 'normal' }}>
                              {displayLabel}
                            </TableCell>
                            <TableCell>
                              {node.type ? (
                                <Chip 
                                  label={node.type} 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: node.type === 'device' ? '#ef4444' : 
                                             node.type === 'phone' ? '#f59e0b' : 
                                             node.type === 'location' ? '#ec4899' : 
                                             node.type === 'website' ? '#8b5cf6' : '#9e9e9e',
                                    color: 'white',
                                    height: 20,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {node.entity_type ? (
                                <Chip 
                                  label={node.entity_type} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {node.place_name && (
                                <Box>
                                  <Typography variant="caption" display="block" fontWeight="bold">
                                    {node.place_name}
                                  </Typography>
                                  {node.place_type && (
                                    <Chip 
                                      label={node.place_type} 
                                      size="small" 
                                      sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }}
                                    />
                                  )}
                                </Box>
                              )}
                              {!node.place_name && '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={node.connections} 
                                size="small" 
                                color={node.connections > 5 ? 'error' : node.connections > 2 ? 'warning' : 'default'}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
            )}


            {/* Map Tab */}
            {activeTab === 1 && (
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <NetworkGraphMap selectedNodes={selectedNodes} nodes={nodes} />
          </Box>
            )}

            {/* Controls Tab */}
            {activeTab === 2 && (
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight="bold">
            Graph Controls
          </Typography>

          {/* Zoom Controls */}
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Zoom
            </Typography>
            <ButtonGroup size="small" fullWidth>
              <Tooltip title="Zoom In">
                <IconButton onClick={handleZoomIn}>
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton onClick={handleZoomOut}>
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset View">
                <IconButton onClick={handleZoomReset}>
                  <CenterFocusStrongIcon />
                </IconButton>
              </Tooltip>
              {onRefreshAnalysis && (
                <Tooltip title="Refresh Analysis">
                  <IconButton onClick={onRefreshAnalysis} sx={{ color: 'warning.main' }}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ButtonGroup>
          </Box>

          {/* Proximity Analysis Controls */}
          {proximityDistance !== undefined && onProximityDistanceChange && (
            <Box>
              <Typography variant="caption" display="block" gutterBottom>
                Proximity Threshold: {proximityDistance}m
              </Typography>
              <Slider
                value={proximityDistance}
                onChange={(_, value) => onProximityDistanceChange(value as number)}
                min={10}
                max={500}
                step={10}
                valueLabelDisplay="auto"
                size="small"
                marks={[
                  { value: 10, label: '10m' },
                  { value: 50, label: '50m' },
                  { value: 100, label: '100m' },
                  { value: 250, label: '250m' },
                  { value: 500, label: '500m' },
                ]}
              />
            </Box>
          )}

          {timeWindow !== undefined && onTimeWindowChange && (
            <Box>
              <Typography variant="caption" display="block" gutterBottom>
                Time Window: {timeWindow}s ({Math.round(timeWindow / 60)}min)
              </Typography>
              <Slider
                value={timeWindow}
                onChange={(_, value) => onTimeWindowChange(value as number)}
                min={60}
                max={1800}
                step={60}
                valueLabelDisplay="auto"
                size="small"
                marks={[
                  { value: 60, label: '1m' },
                  { value: 300, label: '5m' },
                  { value: 600, label: '10m' },
                  { value: 1200, label: '20m' },
                  { value: 1800, label: '30m' },
                ]}
              />
            </Box>
          )}

          {/* Display Filter */}
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Display Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyKnownDevices}
                  onChange={(e) => setShowOnlyKnownDevices(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Show Only Known Devices
                </Typography>
              }
            />
          </Box>

          {/* Selection Controls */}
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Selection Mode
            </Typography>
            <Stack spacing={1}>
              <Button
                size="small"
                variant={selectionModeActive ? 'contained' : 'outlined'}
                color={selectionModeActive ? 'warning' : 'primary'}
                onClick={handleToggleSelectionMode}
                startIcon={<SelectAllIcon />}
                fullWidth
              >
                {selectionModeActive ? 'Exit Selection' : 'Enable Selection'}
              </Button>
              {selectedNodes.size > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearSelection}
                  startIcon={<DeselectIcon />}
                  fullWidth
                >
                  Clear ({selectedNodes.size})
                </Button>
              )}
            </Stack>
          </Box>

          {/* Color Scheme */}
          <FormControl size="small" fullWidth>
            <InputLabel>Color By</InputLabel>
            <Select
              value={colorScheme}
              label="Color By"
              onChange={(e) => setColorScheme(e.target.value)}
            >
              <MenuItem value="category">Connection Type</MenuItem>
              <MenuItem value="type">Node Type</MenuItem>
            </Select>
          </FormControl>

          {/* Link Distance */}
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Link Distance: {linkDistance}
            </Typography>
            <Slider
              value={linkDistance}
              onChange={(_, value) => setLinkDistance(value as number)}
              min={50}
              max={300}
              step={10}
              size="small"
            />
          </Box>

          {/* Charge Strength */}
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Repulsion: {Math.abs(chargeStrength)}
            </Typography>
            <Slider
              value={Math.abs(chargeStrength)}
              onChange={(_, value) => setChargeStrength(-(value as number))}
              min={100}
              max={1000}
              step={50}
              size="small"
            />
          </Box>

          {/* Curvature */}
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Link Curvature: {curvature.toFixed(1)}
            </Typography>
            <Slider
              value={curvature}
              onChange={(_, value) => setCurvature(value as number)}
              min={0}
              max={0.8}
              step={0.1}
              size="small"
            />
          </Box>

          {/* Stats */}
          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Chip 
                label={stats.originalNodes ? `${stats.nodes}/${stats.originalNodes}` : `${stats.nodes}`} 
                size="small" 
                color={stats.originalNodes ? "warning" : "primary"} 
              />
              <Chip 
                label={stats.originalLinks ? `${stats.links}/${stats.originalLinks}` : `${stats.links}`} 
                size="small" 
                color={stats.originalLinks ? "warning" : "secondary"} 
              />
            </Stack>
            {stats.originalNodes && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                Graph limited for performance
              </Typography>
            )}
          </Box>
        </Stack>
              </Box>
            )}

            {/* Help Tab */}
            {activeTab === 3 && (
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Link Analysis Help
                </Typography>
                
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                  Graph Interactions
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    &bull; <strong>Click nodes</strong> in graph to expand and see all connections (overrides filters)
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Click table rows</strong> to highlight node, zoom in, and center with expanding ring
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Drag nodes</strong> to manually reposition and customize layout
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Zoom/Pan</strong>: Mouse wheel to zoom, click and drag background to pan
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Double-click background</strong> to clear selection
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Selection Mode</strong>: Enable to box-select multiple nodes, then export to new tab
                  </Typography>
                </Stack>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                  Advanced Filters (Top Panel)
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    &bull; <strong>Timespan</strong>: 1 Day to 4 Weeks (defaults to 1 Week)
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Connection Type</strong>: All, Call Logs, SMS, Browsing, or Dwells
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Entity Type</strong>: Filter by Registration ID
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Filter by Device</strong>: Show connections for specific devices (multi-select)
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Filter by Location</strong>: Filter by geocoded location names (multi-select)
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Filter by Category</strong>: Filter by location type - lodging, restaurant, store, etc. (multi-select)
                  </Typography>
                </Stack>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                  Data Tables
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    &bull; <strong>Legend</strong>: Connection types, node types, and graph statistics
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Nodes Table</strong>: All devices, phones, locations with labels, entity types, and connection counts
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Map View</strong>: Geographic visualization of all connection types color-coded by type
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Export CSV</strong>: Download any table data for further analysis
                  </Typography>
                </Stack>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                  Location Features
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    &bull; <strong>Geocoded Names</strong>: Locations show actual place names (Starbucks, Walmart, etc.)
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Neighborhood Fallback</strong>: Uses neighborhood names when geocoding unavailable
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Categories</strong>: Locations tagged by type (restaurant, lodging, store, etc.)
                  </Typography>
                  <Typography variant="body2">
                    &bull; <strong>Map Clusters</strong>: Color-coded by connection type (red=dwells, blue=calls, green=SMS)
                  </Typography>
                </Stack>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                  Node Expansion (Click to Explore)
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    When you click a node in the graph, it "expands" to show ALL its connections, even if they're hidden by filters.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    This lets you progressively explore the network - start with a filter (e.g., "lodging"), then expand specific nodes to see their full connections.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Click the node again to collapse, or click "X Nodes Expanded" button to reset all.
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Context Menu for Node Right-Click */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            bgcolor: '#1a1a2e',
            border: '1px solid #00f0ff',
            boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)',
            '& .MuiMenuItem-root': {
              color: '#e6edf3',
              '&:hover': {
                bgcolor: 'rgba(0, 240, 255, 0.1)',
              },
            },
          },
        }}
      >
        {contextMenu && (
          <>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #30363d' }}>
              <Typography variant="caption" sx={{ color: '#8b949e' }}>
                Node: {contextMenu.node.label || contextMenu.node.id}
              </Typography>
              {contextMenu.node.type && (
                <Chip 
                  label={contextMenu.node.type} 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: 'rgba(0, 240, 255, 0.2)',
                    color: '#00f0ff',
                    border: '1px solid #00f0ff'
                  }} 
                />
              )}
            </Box>
            <MenuItem 
              onClick={() => contextMenu && handleExportSubgraph(contextMenu.node)}
              disabled={!onExportSubgraph}
            >
              <ListItemIcon>
                <AccountTreeIcon sx={{ color: '#00f0ff' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Export Subset Graph" 
                secondary={`Node + ${links.filter((l: any) => {
                  const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
                  const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
                  return sourceId === contextMenu.node.id || targetId === contextMenu.node.id;
                }).length} connections`}
                primaryTypographyProps={{ sx: { color: '#e6edf3' } }}
                secondaryTypographyProps={{ sx: { color: '#8b949e', fontSize: '0.7rem' } }}
              />
              <OpenInNewIcon sx={{ color: '#8b949e', fontSize: 16, ml: 1 }} />
            </MenuItem>
            <Divider sx={{ borderColor: '#30363d' }} />
            <MenuItem onClick={() => {
              if (onNodeClick && contextMenu) {
                onNodeClick(contextMenu.node);
              }
              handleCloseContextMenu();
            }}>
              <ListItemIcon>
                <InfoIcon sx={{ color: '#00ff88' }} />
              </ListItemIcon>
              <ListItemText 
                primary="View Details" 
                primaryTypographyProps={{ sx: { color: '#e6edf3' } }}
              />
            </MenuItem>
            
            {/* Device-specific menu items */}
            {contextMenu.node.type === 'device' && (
              <>
                <Divider sx={{ borderColor: '#30363d' }} />
                {onSaveDevice && (
                  <MenuItem onClick={() => {
                    const deviceId = contextMenu.node.id;
                    const deviceLabel = contextMenu.node.label || contextMenu.node.id;
                    onSaveDevice(deviceId, deviceLabel);
                    handleCloseContextMenu();
                  }}>
                    <ListItemIcon>
                      <BookmarkAddIcon sx={{ color: '#10b981' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Save Device" 
                      secondary="Add to watchlist"
                      primaryTypographyProps={{ sx: { color: '#e6edf3' } }}
                      secondaryTypographyProps={{ sx: { color: '#8b949e', fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                )}
                {onAddDeviceNote && (
                  <MenuItem onClick={() => {
                    const deviceId = contextMenu.node.id;
                    const deviceLabel = contextMenu.node.label || contextMenu.node.id;
                    onAddDeviceNote(deviceId, deviceLabel);
                    handleCloseContextMenu();
                  }}>
                    <ListItemIcon>
                      <NoteAddIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Add Note" 
                      secondary="Save notes for this device"
                      primaryTypographyProps={{ sx: { color: '#e6edf3' } }}
                      secondaryTypographyProps={{ sx: { color: '#8b949e', fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                )}
              </>
            )}
            
            {/* Location-specific menu items */}
            {contextMenu.node.type === 'location' && (
              <>
                <Divider sx={{ borderColor: '#30363d' }} />
                {onSaveAsGeofence && (
                  <MenuItem onClick={() => {
                    const node = contextMenu.node as any;
                    // Location nodes have coordinates in [lat, lng] format from cluster.center
                    const lat = node.coordinates?.[0] ?? 0;
                    const lng = node.coordinates?.[1] ?? 0;
                    const name = node.place_name || node.label || `Location ${node.id}`;
                    const placeType = node.place_type;
                    onSaveAsGeofence({ lat, lng, name, placeType });
                    handleCloseContextMenu();
                  }}>
                    <ListItemIcon>
                      <BookmarkAddIcon sx={{ color: '#ec4899' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Save as Geofence" 
                      secondary="Monitor this location"
                      primaryTypographyProps={{ sx: { color: '#e6edf3' } }}
                      secondaryTypographyProps={{ sx: { color: '#8b949e', fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                )}
                {onAddLocationNote && (
                  <MenuItem onClick={() => {
                    const node = contextMenu.node as any;
                    const lat = node.coordinates?.[0] ?? 0;
                    const lng = node.coordinates?.[1] ?? 0;
                    const name = node.place_name || node.label || `Location ${node.id}`;
                    onAddLocationNote({ lat, lng, name });
                    handleCloseContextMenu();
                  }}>
                    <ListItemIcon>
                      <NoteAddIcon sx={{ color: '#ec4899' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Add Location Note" 
                      secondary="Save notes about this place"
                      primaryTypographyProps={{ sx: { color: '#e6edf3' } }}
                      secondaryTypographyProps={{ sx: { color: '#8b949e', fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                )}
              </>
            )}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default NetworkGraph;


